import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { sessionService } from "redux-react-session";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { object, string } from "yup";

import { callAPIInterface } from "../constants";
import { Input } from "../input/Input";
import {
  accountName,
  accountNameChanged,
  accountDetails,
  countryLabel,
  submit,
} from "../messages";
import Button from "../button/Button";
import { useSession } from "../../hooks/useSession";
import { setCurrentAccountName, showToast } from "../../redux/actions";
import CommonHeader from "../CommonHeader";
import CustomDivider from "../divider/Divider";
import SelectStateDropdown from "../SelectStateDropdown";

function ChangeAccountName() {
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [country, setCountry] = useState(null);
  const [state, setState] = useState(null);
  const [buttonLoader, setButtonLoader] = useState(false);

  const { session } = useSession();
  const account = session?.account?.account;

  const validationSchema = object({
    name: string().trim().required(),
    country: string().required(),
    state: string().when("statesAvailable", {
      is: true,
      then: () => string().required(),
    }),
  });

  useEffect(() => {
    if (account) {
      setName(account?.name);
      setCountry(account?.country);
      setState(account?.state);
    }
  }, [session]);

  const handleProfileDataChange = async (val) => {
    const data = {
      name: val?.name || undefined,
      state: val?.state || undefined,
    };
    try {
      setButtonLoader(true);
      const response = await callAPIInterface("PUT", "/account", data);
      dispatch(
        showToast({
          isToastOpen: true,
          toastMessage: accountNameChanged,
          toastVariant: "success",
        })
      );
      const updatedAccountDetails = session?.user?.accounts_details?.map(
        (accountData) => {
          return {
            ...accountData,
            ...(accountData?.account?.id === response?.id && {
              account: response,
            }),
          };
        }
      );
      dispatch(setCurrentAccountName(response));
      await sessionService.saveSession({
        ...session,
        account: { ...session?.account, account: response },
        user: {
          ...session?.user,
          accounts_details: updatedAccountDetails,
        },
      });
    } catch (error) {
      console.error("Error updating profile data:", error);
    } finally {
      setButtonLoader(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: name ?? null,
      country: country ?? null,
      state: state ?? null,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleProfileDataChange,
  });

  const { values, isValid, dirty, handleChange, handleSubmit } = formik;

  return (
    <>
      <CommonHeader textAfterIcon={accountDetails} icon="backArrow" />
      <Box className="options-container">
        <Box className="change-account-wrapper">
          <Box className="module-container">
            <Box className="module-container-content">
              <Input
                showLabel
                label={accountName}
                fullWidth
                name="name"
                value={values.name}
                type="text"
                onChange={handleChange}
                inputProps={{ maxLength: 250 }}
              />
              <Input
                showLabel
                label={countryLabel}
                fullWidth
                name="country"
                value={values.country}
                type="text"
                disabled={true}
                customClass="mt-24"
              />
              <SelectStateDropdown
                formik={formik}
                customClass="register-country-input"
                handleChange={handleChange}
              />
            </Box>
            <Box className="module-container-footer">
              <CustomDivider className="settings-divider" />
              <Button
                fullWidth
                loading={buttonLoader}
                disabled={!(isValid && dirty)}
                variant="contained"
                className="settings-button"
                label={submit}
                onClick={handleSubmit}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default ChangeAccountName;
